import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, MessageSquare } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

const when = (iso) => (iso ? new Date(iso).toLocaleString([], {
  month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

/** Internal notes / review conversation on an article. Editors only. */
export function KbCommentsPanel({ slug, refreshKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/kb/articles/${slug}/comments`)
    .then((r) => setRows(r.data.comments || [])).catch(() => {});

  useEffect(() => { if (slug) load(); }, [slug, refreshKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    try {
      await api.post(`/kb/articles/${slug}/comments`, { body });
      setDraft("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const resolve = async (id) => {
    try {
      await api.post(`/kb/articles/${slug}/comments/${id}/resolve`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-2" data-testid="kb-comments-panel">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
        <MessageSquare className="h-3.5 w-3.5" /> Internal notes
      </div>
      <ul className="max-h-64 space-y-1.5 overflow-y-auto">
        {rows.length === 0 && (
          <li className="text-xs text-slate-400 dark:text-slate-500" data-testid="kb-comments-empty">
            No notes yet — leave one for whoever picks this up next.
          </li>
        )}
        {rows.map((c) => (
          <li key={c.comment_id} data-testid={`kb-comment-${c.comment_id}`}
            className={`rounded-md border px-2.5 py-2 text-xs ${
              c.resolved ? "border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 text-slate-400 dark:text-slate-500" : "border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300"}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-slate-700 dark:text-slate-200">{c.by_name}</span>
              <span className="flex items-center gap-1">
                <span className="text-slate-400 dark:text-slate-500">{when(c.at)}</span>
                {!c.resolved && (
                  <button type="button" title="Mark resolved" onClick={() => resolve(c.comment_id)}
                    data-testid={`kb-comment-resolve-${c.comment_id}`}
                    className="text-slate-400 dark:text-slate-500 hover:text-emerald-600">
                    <Check className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            </div>
            <p className="mt-1 whitespace-pre-wrap">{c.body}</p>
            {c.resolved && <p className="mt-1 text-[10px] uppercase tracking-wide">Resolved by {c.resolved_by}</p>}
          </li>
        ))}
      </ul>
      <textarea data-testid="kb-comment-input" value={draft} rows={2}
        placeholder="Add a note for your co-workers…"
        onChange={(e) => setDraft(e.target.value)}
        className="w-full rounded-md border border-slate-200 dark:border-slate-800 px-2.5 py-2 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary" />
      <Button size="sm" variant="outline" className="h-7 w-full text-xs" data-testid="kb-comment-add"
        onClick={add} disabled={busy || !draft.trim()}>
        {busy ? "Saving…" : "Add note"}
      </Button>
    </div>
  );
}
